import { auth, db } from "@/src/lib/firebase";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  toRealtimeLoadError,
  type RealtimeLoadError,
} from "@/src/lib/realtimeRecovery";

export type AppNotificationRoute =
  | {
      pathname: "/(tabs)/messages/[id]";
      params: { id: string };
    }
  | {
      pathname: "/(tabs)/messages/direct/[id]";
      params: { id: string; identityId: string };
    }
  | {
      pathname: "/post/[postId]";
      params: { postId: string };
    }
  | {
      pathname: "/(tabs)/notifications";
    };

export type AppNotification = {
  id: string;
  type: "message" | "direct_message" | "comment" | "follow";
  title: string;
  body: string;
  createdAtMs: number;
  route: AppNotificationRoute;
  read: boolean;
  opened: boolean;
};

function toMillis(value: any) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  return 0;
}

function isRoute(value: any): value is AppNotificationRoute {
  if (!value || typeof value !== "object") return false;
  if (value.pathname === "/(tabs)/notifications") return true;
  if (!value.params || typeof value.params !== "object") return false;
  if (value.pathname === "/(tabs)/messages/direct/[id]") {
    return typeof value.params.id === "string" && typeof value.params.identityId === "string";
  }
  return value.pathname === "/(tabs)/messages/[id]" || value.pathname === "/post/[postId]";
}

export function useNotifications() {
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RealtimeLoadError | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const previousUidRef = useRef<string | null>(null);
  const uid = auth.currentUser?.uid ?? null;

  useEffect(() => {
    const userChanged = previousUidRef.current !== uid;
    previousUidRef.current = uid;
    if (userChanged) setItems([]);
    setError(null);
    setLoading(true);

    if (!uid) {
      setLoading(false);
      return;
    }

    let active = true;
    const notificationsQuery = query(
      collection(db, "users", uid, "notifications"),
      orderBy("createdAt", "desc"),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snap) => {
        if (!active) return;
        const nextItems = snap.docs
          .map((docSnap) => {
            const data = docSnap.data() as any;
            if (!isRoute(data?.route)) return null;
            const type: AppNotification["type"] = data?.type === "comment"
              ? "comment"
              : data?.type === "follow"
                ? "follow"
                : data?.type === "direct_message"
                  ? "direct_message"
                  : "message";
            return {
              id: docSnap.id,
              type,
              title: String(data?.title ?? "").trim() || "Notification",
              body: String(data?.body ?? "").trim(),
              createdAtMs: toMillis(data?.createdAt),
              route: data.route,
              read: !!data?.readAt,
              opened: !!data?.openedAt,
            } satisfies AppNotification;
          })
          .filter(Boolean) as AppNotification[];

        setItems(nextItems);
        setError(null);
        setLoading(false);
      },
      (snapshotError) => {
        if (!active) return;
        active = false;
        if (__DEV__) console.warn("[NOTIFICATIONS] snapshot error:", snapshotError?.code ?? "unknown");
        setError(toRealtimeLoadError(snapshotError, "Impossible de charger les notifications."));
        setLoading(false);
      }
    );

    return () => {
      active = false;
      unsubscribe();
    };
  }, [retryKey, uid]);

  const retry = useCallback(() => {
    setError(null);
    setLoading(true);
    setRetryKey((key) => key + 1);
  }, []);

  const unreadCount = useMemo(() => items.filter((item) => !item.read).length, [items]);

  return { items, count: items.length, unreadCount, loading, error, retry };
}
